/**
 * Utility for determining call success descriptors based on AI analysis
 */

/**
 * Normalizes status text for display (e.g., "call_initiation_failure" → "Call Initiation Failure")
 * @param {string} value - Raw status text
 * @returns {string} Normalized display text
 */
export const normalizeStatusText = (value) => {
    if (!value || typeof value !== "string") return "";
    const cleaned = value
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!cleaned) return "";
    return cleaned
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

/**
 * Extracts the transcript summary from an AI call record
 * @param {object} item - The call record object
 * @returns {string} Transcript summary or empty string
 */
export const getTranscriptSummary = (item) => {
    if (!item) return "";
    const transcript = item.elevenlabs_call_transcription || item.call_transcript;
    if (!transcript) return "";
    try {
        const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
        if (parsed && typeof parsed.summary === "string") {
            return parsed.summary;
        }
    } catch (err) { }
    return "";
};

/**
 * Gets a descriptor for successful (connected) calls based on AI analysis
 * @param {object} item - The call record object
 * @returns {string} Human-readable success descriptor
 */
export const getSuccessDescriptor = (item) => {
    if (!item) {
        return "Call";
    }

    // Rule 1: Check for no-answer failure
    if (
        item.type === "call_initiation_failure" &&
        (item.failure_reason === "no-answer" || item.failure_reason === "no answer")
    ) {
        return "Not Concluded";
    }

    // Rule 2: Check for voicemail detection
    if (item.voicemail_detection === true) {
        return "Not Concluded";
    }

    // Rule 3: Check elevenlabs_analysis
    if (item.elevenlabs_analysis) {
        try {
            const analysis =
                typeof item.elevenlabs_analysis === "string"
                    ? JSON.parse(item.elevenlabs_analysis)
                    : item.elevenlabs_analysis;

            const callScheduleEval =
                analysis?.evaluation_criteria_results?.call_schedule_evaluation?.result;
            const clientEngagementEval =
                analysis?.evaluation_criteria_results?.client_engagement_evaluation?.result;

            if (callScheduleEval && clientEngagementEval) {
                const normalizedCallSchedule = String(callScheduleEval).trim().toLowerCase();
                const normalizedClientEngagement = String(clientEngagementEval).trim().toLowerCase();

                if (normalizedCallSchedule === "success" && normalizedClientEngagement === "success") {
                    return "Call Concluded";
                } else if (normalizedCallSchedule === "failure" && normalizedClientEngagement === "failure") {
                    return "Not Interested";
                } else if (
                    normalizedCallSchedule === "failure" &&
                    normalizedClientEngagement === "success"
                ) {
                    return "Not Interested";
                } else {
                    return "Not Concluded";
                }
            }
        } catch (error) {
            // Error parsing elevenlabs_analysis - continue to fallback
        }
    }

    // Fallback to other status fields
    const summary = getTranscriptSummary(item);
    if (summary) {
        const summaryLc = summary.toLowerCase();
    }

    const message = normalizeStatusText(
        item?.elevenlabs_message || item?.status_message || item?.call_status_message
    );
    if (message) return message;

    const typeDescriptor = normalizeStatusText(item?.type || item?.callOutcome || item?.status);
    if (typeDescriptor) return typeDescriptor;

    return "Call";
};
